import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'

import HealthCheckRow from './HealthCheckRow'
import { GET, httpOk } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

// «Проверка настроек»: плитка услуг, проверка — по клику.
//
// Почему плитка, а не список строк. Услугу выбирают глазами — по иконке и названию,
// как приложение на телефоне; список одинаковых строк приходится читать целиком.
// У бренда с десятком интеграций плитка помещается на экран, а список — нет, поэтому
// сверху поиск и фильтр по группам.
//
// Открытие страницы ничего не считает: это один дешёвый запрос за списком услуг. Первая
// версия считала всё сразу и выдавала полсотни строк, включая услуги, которыми клиент
// не пользуется. Считается только то, на что нажали.
//
// Фискализация и оборудование при клике ещё и спрашивают саму кассу (probe=1): стоит ли
// драйвер ОФД, отвечает ли он на тестовый запрос, откликаются ли принтеры.

// Услуги, проверка которых идёт до кассы и потому занимает несколько секунд
const PROBE_MODULES = ['fiscal', 'equipment']

// Фильтры по группам. `problems` — не группа, а состояние: показать то, где нашлись ошибки
const GROUPS = ['all', 'pos', 'delivery', 'channel', 'courier', 'other', 'problems']

// Порядок разделов в плитке: сначала то, без чего касса не работает, потом каналы продаж
const SECTION_ORDER = ['pos', 'delivery', 'channel', 'courier', 'other']

// Счётчики над чек-листом: они же фильтр по статусу
const CHECK_FILTERS = [
	{ status: 'error', nameKey: 'setup_health_filter_errors', activeClass: 'btn-danger', idleClass: 'btn-outline-danger' },
	{ status: 'warning', nameKey: 'setup_health_filter_warnings', activeClass: 'btn-warning', idleClass: 'btn-outline-warning' },
	{ status: 'ok', nameKey: 'setup_health_filter_ok', activeClass: 'btn-success', idleClass: 'btn-outline-success' },
	{ status: 'skipped', nameKey: 'setup_health_filter_skipped', activeClass: 'btn-secondary', idleClass: 'btn-outline-secondary' },
]

// Карточки одной высоты: иначе ряд «прыгает» из-за длинных названий интеграций,
// а плитка перестаёт читаться как сетка
const CARD_HEIGHT = 190

function Index() {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const [pos, setPos] = useState(null)
	const [modules, setModules] = useState([])
	// Результаты проверок: key модуля -> ответ бэка. Пока не нажали — пусто
	const [results, setResults] = useState({})
	const [running, setRunning] = useState(null)
	const [search, setSearch] = useState('')
	const [group, setGroup] = useState('all')

	const [showModal, setShowModal] = useState(false)
	const [selectedModule, setSelectedModule] = useState(null)
	// Фильтр чек-листа внутри окна: null — показывать все шаги подряд
	const [checkFilter, setCheckFilter] = useState(null)

	async function getModules() {
		const response = await GET('/setup-health', {}, { loader: true })

		if (httpOk(response)) {
			setPos(response.data.pos)
			setModules(response.data.modules)
		}
	}

	// Клик по карточке: открываем окно и запускаем проверку. Уже проверенную повторно
	// не гоняем — только показываем, иначе каждый повторный клик дёргал бы кассу
	async function openModule(module, force = false) {
		setSelectedModule(module)
		setCheckFilter(null)
		setShowModal(true)

		if (results[module.key] && !force) return

		setRunning(module.key)

		const isProbe = PROBE_MODULES.includes(module.key)
		// Кассу спрашиваем один раз на заход: ответ приходит целиком (и драйвер, и принтеры),
		// поэтому «Фискализация», открытая следом за «Оборудованием», берёт свежий снимок.
		// `force` — это «Проверить заново», он всегда идёт до кассы
		const url = isProbe
			? `/setup-health/${module.key}?probe=1${force ? '&force=1' : ''}`
			: `/setup-health/${module.key}`
		const response = await GET(url, {}, { loader: false })

		if (httpOk(response)) {
			setResults((prev) => ({ ...prev, [module.key]: response.data }))
		}

		setRunning(null)
	}

	function moduleTitle(module) {
		return module.title || t(`setup_health_module_${module.key}`)
	}

	// Подпись под названием: у интеграции — что это за услуга, у постоянных модулей —
	// короткое пояснение из локали
	function moduleAbout(module) {
		return t(`setup_health_about_${module.group}`)
	}

	// Порядок проверок НЕ трогаем: бэк отдаёт их шагами настройки — сначала оплачена ли
	// услуга, потом настройки точки, терминала и товаров, и только в конце то, что зависит
	// от кассы. Так список читается как инструкция «что делать дальше».
	//
	// Раньше здесь была сортировка «ошибки вперёд»: красное было видно сразу, но шаги
	// перемешивались, и «услуга подключена» оказывалась в середине списка. Красное и так
	// видно по цвету, а найти только его помогают счётчики-фильтры выше.
	function visibleChecks(result) {
		if (!checkFilter) return result.checks || []

		return (result.checks || []).filter((check) => check.status === checkFilter)
	}

	function checkCount(result, status) {
		if (status === 'error') return result.count_error
		if (status === 'warning') return result.count_warning
		if (status === 'ok') return result.count_ok

		return result.count_skipped
	}

	function statusButton(module) {
		const result = results[module.key]

		if (running === module.key) {
			return (
				<span className="btn btn-sm btn-light w-100 text-muted">
					<span className="spinner-border spinner-border-sm me-1"></span>
					{PROBE_MODULES.includes(module.key) ? t('setup_health_asking_pos') : t('setup_health_checking')}
				</span>
			)
		}

		if (!result) {
			return (
				<span className="btn btn-sm btn-outline-primary w-100">
					{module.is_configured ? t('setup_health_run') : t('setup_health_not_configured')}
				</span>
			)
		}

		if (result.count_error > 0) {
			return (
				<span className="btn btn-sm btn-danger w-100">
					<i className="uil uil-times-circle me-1"></i>
					{t('setup_health_errors', { count: result.count_error })}
				</span>
			)
		}

		if (result.count_warning > 0) {
			return (
				<span className="btn btn-sm btn-warning w-100">
					<i className="uil uil-exclamation-triangle me-1"></i>
					{t('setup_health_warnings', { count: result.count_warning })}
				</span>
			)
		}

		return (
			<span className="btn btn-sm btn-success w-100">
				<i className="uil uil-check me-1"></i>
				{t('setup_health_all_ok')}
			</span>
		)
	}

	const query = search.trim().toLowerCase()

	const visibleModules = modules.filter((module) => {
		if (query && !moduleTitle(module).toLowerCase().includes(query)) return false

		if (group === 'all') return true
		if (group === 'problems') return (results[module.key]?.count_error ?? 0) > 0

		return module.group === group
	})

	// Раскладываем видимые карточки по разделам, сохраняя порядок SECTION_ORDER
	const visibleSections = SECTION_ORDER
		.map((item) => ({
			group: item,
			modules: visibleModules.filter((module) => module.group === item),
		}))
		.filter((section) => section.modules.length > 0)

	const result = selectedModule ? results[selectedModule.key] : null
	const isRunning = selectedModule ? running === selectedModule.key : false

	useEffect(() => {
		getModules()
	}, [reduxAccount.pos_id]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				{/* Точка — рядом с заголовком в одну строку: это не подзаголовок раздела,
					а уточнение «чьи настройки смотрим».
					Здесь НЕ `vertical-center` из общих стилей: он flex-direction column,
					и подпись уезжала под заголовок */}
				<h4 className="d-flex align-items-center mb-0 h-100">
					{t('setup_health')}
					{pos?.name &&
						<span className="ms-2 text-muted fs-6">{pos.name}</span>
					}
				</h4>
			</div>

			{/* Поиск и фильтры — одной строкой над плиткой, а не в углу шапки: они относятся
				к списку карточек, и рука идёт к ним сверху вниз, а не через весь экран.
				У бренда бывает десяток интеграций, и без фильтров плитка превращается
				в поле одинаковых карточек */}
			<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
				<div className="position-relative" style={{ 'width': '250px' }}>
					<i className="uil-search input-search-icon"></i>
					<input type="text" className="form-control form-control-sm"
						placeholder={t('search') + '...'}
						value={search}
						onChange={(e) => setSearch(e.target.value)} />
				</div>

				{GROUPS.map((item) => (
					<button key={item}
						className={`btn btn-sm ${group === item ? 'btn-primary' : 'btn-outline-secondary'}`}
						onClick={() => setGroup(item)}>
						{t(`setup_health_group_${item}`)}
					</button>
				))}
			</div>

			{/* Плитка разбита на разделы: у бренда с десятком интеграций сплошная сетка
				читается как свалка. Заголовки показываем только когда разделов больше одного —
				при выбранном фильтре или поиске подпись «Доставки» над единственным блоком
				лишняя */}
			{visibleSections.map((section) => (
				<div key={section.group}>
					{visibleSections.length > 1 &&
						<h6 className="text-muted mb-2">{t(`setup_health_group_${section.group}`)}</h6>
					}

					<div className="row">
						{section.modules.map((module) => (
							<div className="col-xl-3 col-lg-4 col-md-6 mb-3" key={module.key}>
								<div className="card cursor-pointer" style={{ 'height': CARD_HEIGHT }}
									onClick={() => openModule(module)}>
									<div className="card-body d-flex flex-column p-3">
										{/* Иконка в мягкой плашке — как у витрины модулей: значок
											читается быстрее подписи */}
										<div className="d-flex align-items-center justify-content-center rounded mb-2"
											style={{ 'width': '44px', 'height': '44px', 'backgroundColor': 'var(--dark-surface-2, #f3f5f9)' }}>
											<i className={`uil ${module.icon || 'uil-apps'} text-primary fs-4`}></i>
										</div>

										<div className="text-truncate" title={moduleTitle(module)}>
											{moduleTitle(module)}
										</div>

										<div className="fs-6 text-muted text-truncate">
											{moduleAbout(module)}
										</div>

										<div className="mt-auto">
											{statusButton(module)}
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			))}

			{visibleModules.length === 0 &&
				<div className="text-center text-muted">{t('list_empty')}</div>
			}

			{/* MODAL — чек-лист выбранной услуги. Закрывается кликом мимо окна.
				`scrollable` оставляет шапку и кнопки на месте, а прокручивает только список */}
			<Modal show={showModal} animation={false} centered scrollable onHide={() => setShowModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedModule &&
							<>
								<i className={`uil ${selectedModule.icon || 'uil-apps'} me-2`}></i>
								{moduleTitle(selectedModule)}
							</>
						}
					</Modal.Title>
				</Modal.Header>
				{/* Высота фиксированная, а не по содержимому: пока идёт опрос кассы, окно
					было бы в две строки и рывком вырастало до полного чек-листа — прыжок
					раздражает сильнее, чем пустое поле под коротким списком */}
				<Modal.Body style={{ 'height': '500px' }}>
					{isRunning &&
						<div className="text-center text-muted py-4">
							<span className="spinner-border spinner-border-sm me-2"></span>
							{selectedModule && PROBE_MODULES.includes(selectedModule.key)
								? t('setup_health_asking_pos')
								: t('setup_health_checking')}
						</div>
					}

					{!isRunning && result &&
						<>
							{/* Счётчики они же фильтр: список идёт шагами настройки, а когда
								нужно только красное — один клик */}
							<div className="d-flex flex-wrap gap-2 mb-2">
								{CHECK_FILTERS.map((item) => (
									<button key={item.status}
										className={`btn btn-sm ${checkFilter === item.status ? item.activeClass : item.idleClass}`}
										onClick={() => setCheckFilter(checkFilter === item.status ? null : item.status)}>
										{t(item.nameKey)}: {checkCount(result, item.status)}
									</button>
								))}
							</div>

							{visibleChecks(result).map((check) => (
								<HealthCheckRow key={check.code} check={check} />
							))}

							{/* Для фискализации и оборудования это время ответа кассы */}
							{selectedModule && PROBE_MODULES.includes(selectedModule.key) &&
								<div className="text-muted fs-6 mt-2">
									{result.probe
										? t('setup_health_probe_checked_at', { date: formatDate(result.probe.checked_at, 'dd.MM.yyyy HH:mm') })
										: t('setup_health_probe_timeout')}
								</div>
							}
						</>
					}
				</Modal.Body>
				<Modal.Footer>
					<button className="btn btn-outline-secondary"
						disabled={isRunning}
						onClick={() => openModule(selectedModule, true)}>
						<i className="uil uil-sync me-1"></i>
						{t('setup_health_check_again')}
					</button>
					<button className="btn btn-primary" onClick={() => setShowModal(false)}>
						{t('close')}
					</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

export default Index
